import React, { useState } from "react";
import {
  Bell,
  CircleUser,
  LogOut,
  ExternalLink,
  Menu,
  Mail,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Images from "../Images";
import MobileSidebar from "./MobileSidebar";
import { logoutUser } from "../requests/auth/Authentication";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";

const Navbar = ({page}) => {
  const user = useSelector((state) => state.user.user);
  // console.log("user_details", user);
  
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const navigate = useNavigate();



  const handleAccount = () => {
    setAccountOpen(!accountOpen);
    setNotificationsOpen(false);
  };

  const handleNotifications = () => {
    setNotificationsOpen(!notificationsOpen);
    setAccountOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logoutUser();
      setAccountOpen(false);
      navigate("/signin");
    } catch (error) {
      toast.error("Failed to logout");
    }
  };

  const dropdownVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
  };

  const isMobileView = () => {
    return window.innerWidth < 768;
  };

  const renderNotificationsDropdown = () => {
    if (!notificationsOpen) return null;

    const mobileStyles = isMobileView()
      ? "fixed left-4 right-4 top-16"
      : "absolute right-0 w-80";

    return (
      <motion.div
        initial="hidden"
        animate="visible"
        exit="exit"
        variants={dropdownVariants}
        className={`bg-white rounded-lg border shadow-lg ${mobileStyles}`}
      >
        <div className="p-4 bg-gradient-to-r from-[#FF6C6C] to-pink-500">
          <h2 className="text-lg font-semibold text-white">Notifications</h2>
        </div>

        <div className="max-h-[350px] overflow-y-auto">
          <div className="p-3 border-b transition duration-200 cursor-pointer hover:bg-gray-50">
            <div className="flex gap-3 items-start">
              <div className="mt-2 w-2 h-2 bg-blue-500 rounded-full"></div>
              <div>
                <p className="text-sm font-medium text-gray-800">
                  Your have just redeemed order #123! 🎉
                </p>
                <p className="mt-1 text-xs text-gray-500">2 minutes ago</p>
              </div>
            </div>
          </div>

          <div className="p-3 border-b transition duration-200 cursor-pointer hover:bg-gray-50">
            <div className="flex gap-3 items-start">
              <div className="mt-2 w-2 h-2 bg-green-500 rounded-full"></div>
              <div>
                <p className="text-sm font-medium text-gray-800">
                  Special offer: Get 20% off on Electronics!
                </p>
                <p className="mt-1 text-xs text-gray-500">1 hour ago</p>
              </div>
            </div>
          </div>

          <div className="p-3 border-b transition duration-200 cursor-pointer hover:bg-gray-50">
            <div className="flex gap-3 items-start">
              <div className="mt-2 w-2 h-2 bg-purple-500 rounded-full"></div>
              <div>
                <p className="text-sm font-medium text-gray-800">
                  New restaurant joined near you: "The Tasty Corner"
                </p>
                <p className="mt-1 text-xs text-gray-500">2 hours ago</p>
              </div>
            </div>
          </div>
        </div>

        <Link
          to="/notification"
          className="flex items-center justify-center gap-2 p-3 text-[#FF6C6C] hover:bg-pink-50 transition duration-200"
          onClick={() => {
            setNotificationsOpen(false);
            setMobileNavOpen(false);
            navigate("/notification");
          }}
        >
          View All Notifications
          <ExternalLink size={16} />
        </Link>
      </motion.div>
    );
  };

  return (
    <>
      <div className="sticky top-0 z-40 flex items-center justify-between w-full bg-white shadow-sm h-14 px-4 md:px-5">
        {/* Left Section */}
        <div className="flex gap-4 items-center">
          <button
            onClick={() => setMobileSidebarOpen(true)}
            className="p-2 text-gray-700 hover:text-gray-900 md:hidden"
          >
            <Menu size={24} />
          </button>
          <img
            src={Images.logo}
            alt="Dealboxx - Logo"
            className="h-8 md:hidden"
          />
          
          <div className="hidden md:flex items-center gap-3">
            <div className="h-8 w-[2px] bg-gradient-to-b from-[#FF6C6C] to-[#FF9B9B]"></div>
            <div className="flex flex-col">
              <motion.h1 
                className="text-sm font-semibold text-gray-800"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                {page || 'Dashboard'}
              </motion.h1>
              <motion.div 
                className="h-[2px] w-12 bg-gradient-to-r from-[#FF6C6C] to-[#FF9B9B]"
                initial={{ width: 0 }}
                animate={{ width: 48 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              />
            </div>
          </div>
        </div>

        {/* Right Section */}
        <div className="flex gap-4 items-center">
            <div className="hidden gap-4 items-center md:flex">
            <div className="mx-auto max-w-md">
              <div className="hidden relative md:flex">
                <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                  <svg
                    className="w-4 h-4 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                <input
                  type="search"
                  className="w-44 py-1.5 pl-9 pr-3 text-xs text-gray-700 bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-[#FF6C6C] focus:ring-1 focus:ring-[#FF6C6C]"
                  placeholder="Search..."
                />
              </div>
            </div>

            {/* Notifications */}
            <div className="relative">
              <button onClick={handleNotifications} className="relative">
                <Bell size={18} className="text-gray-700" />
                <span className="flex absolute -top-1 -right-1 justify-center items-center w-4 h-4 text-[10px] text-white bg-red-500 rounded-full">
                  3
                </span>
              </button>
              {notificationsOpen && renderNotificationsDropdown()}
            </div>

            <div className="relative">
              <button
                onClick={handleAccount}
                className={`transition-colors duration-200 ${
                  accountOpen
                    ? "ring-2 ring-[#FF6C6C] rounded-full"
                    : "hover:ring-2 hover:ring-gray-200 rounded-full"
                }`}
              >
                <img
                  src={user.avatar || "https://api.dicebear.com/7.x/avataaars/svg?seed=JohnDoe&backgroundColor=FF6C6C"}
                  alt={user.full_name}
                  className="w-8 h-8 rounded-full"
                />
              </button>
              {accountOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  className="absolute right-0 mt-2 w-72 bg-white rounded-lg border shadow-lg"
                >
                  <div className="p-4 bg-gradient-to-r from-[#FF6C6C] to-pink-500">
                    <div className="flex gap-3 items-center">
                      <img
                        src={user.avatar || "https://api.dicebear.com/7.x/avataaars/svg?seed=JohnDoe&backgroundColor=FF6C6C"}
                        alt={user.full_name}
                        className="w-8 h-8 rounded-full border-2 border-white"
                      />
                      <div className="text-white">
                        <h3 className="font-semibold">{user.full_name}</h3>
                        <div className="flex gap-1 items-center text-xs">
                          <Mail size={12} />
                          <span>{user.email}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Menu Items */}
                  <ul className="py-2">
                    <li>
                      <Link
                        to="/profile-settings"
                        className="flex items-center gap-2 px-4 py-2 text-gray-800 hover:bg-[#FF6C6C] hover:text-white"
                        onClick={() => setAccountOpen(false)}
                      >
                        <CircleUser size={20} />
                        <span>My Profile</span>
                      </Link>
                    </li>
                    <li>
                      <button
                        onClick={handleLogout}
                        className="flex w-full items-center gap-2 px-4 py-2 text-gray-800 hover:bg-[#FF6C6C] hover:text-white"
                      >
                        <LogOut size={20} />
                        <span>Logout</span>
                      </button>
                    </li>
                  </ul>
                </motion.div>
              )}
            </div>
          </div>

          {/* Mobile Icons */}
          <div className="flex gap-4 items-center md:hidden">
            <div className="relative">
              <button onClick={handleNotifications}>
                <Bell size={18} className="text-gray-700" />
                <span className="flex absolute -top-1 -right-1 justify-center items-center w-4 h-4 text-[10px] text-white bg-red-500 rounded-full">
                  3
                </span>
              </button>
              {notificationsOpen && renderNotificationsDropdown()}
            </div>

            <div className="relative">
              <button
                onClick={handleAccount}
                className={`transition-colors duration-200 ${
                  accountOpen
                    ? "ring-2 ring-[#FF6C6C] rounded-full"
                    : "hover:ring-2 hover:ring-gray-200 rounded-full"
                }`}
              >
                <img
                  src={user.avatar || "https://api.dicebear.com/7.x/avataaars/svg?seed=JohnDoe&backgroundColor=FF6C6C"}
                  alt={user.full_name}
                  className="w-8 h-8 rounded-full"
                />
              </button>
              {accountOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  className="fixed left-4 right-4 top-16 bg-white border rounded-lg shadow-lg md:absolute md:right-0 md:w-64 md:left-auto md:top-auto md:mt-2"
                >
                  <div className="p-4 bg-gradient-to-r from-[#FF6C6C] to-pink-500">
                    <div className="flex gap-3 items-center">
                      <img
                        src={user.avatar || "https://api.dicebear.com/7.x/avataaars/svg?seed=JohnDoe&backgroundColor=FF6C6C"}
                        alt={user.full_name}
                        className="w-8 h-8 rounded-full border-2 border-white"
                      />
                      <div className="text-white">
                        <h3 className="font-semibold">{user.full_name}</h3>
                        <div className="flex gap-1 items-center text-xs">
                          <Mail size={12} />
                          <span>{user.email}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Menu Items */}
                  <ul className="py-2">
                    <li>
                      <Link
                        to="/profile"
                        className="flex items-center gap-2 px-4 py-2 text-gray-800 hover:bg-[#FF6C6C] hover:text-white"
                        onClick={() => setAccountOpen(false)}
                      >
                        <CircleUser size={20} />
                        <span>My Profile</span>
                      </Link>
                    </li>
                    <li>
                      <button
                        onClick={handleLogout}
                        className="flex w-full items-center gap-2 px-4 py-2 text-gray-800 hover:bg-[#FF6C6C] hover:text-white"
                      >
                        <LogOut size={20} />
                        <span>Logout</span>
                      </button>
                    </li>
                  </ul>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {mobileSidebarOpen && (
          <MobileSidebar
            isOpen={mobileSidebarOpen}
            onClose={() => setMobileSidebarOpen(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
